/**
 * CareMap header carousel — Firestore `publicConfig/mapCarousel`.
 *
 * Seed: create doc `publicConfig/mapCarousel` with `slides` copied from
 * HEALTHDESK_DEFAULT_SLIDE_ROWS (and optional mergeMode / minPaidAds), or use
 * “Restore defaults” in Carousel Global Ads admin UI after deploy.
 */

export const MAP_CAROUSEL_COLLECTION = "publicConfig";
export const MAP_CAROUSEL_DOC_ID = "mapCarousel";
/** Aggregates impressions / clicks / signups per global slide id (Cloud Function writes). */
export const MAP_CAROUSEL_STATS_DOC_ID = "mapCarouselStats";

/** CareMap viewer segment for targeting (not always 1:1 with Firestore user.role). */
export const CAROUSEL_TARGET_PATIENT = "patient";
export const CAROUSEL_TARGET_PROFESSIONAL = "professional";
/** Canonical Firestore value for org / facility audience (legacy: `manager`). */
export const CAROUSEL_TARGET_FACILITY = "facility";

/** @deprecated Use CAROUSEL_TARGET_FACILITY. Same canonical value `facility`. */
export const CAROUSEL_TARGET_MANAGER = CAROUSEL_TARGET_FACILITY;

export const CAROUSEL_TARGETS = [
  CAROUSEL_TARGET_PATIENT,
  CAROUSEL_TARGET_PROFESSIONAL,
  CAROUSEL_TARGET_FACILITY,
];

export const CAROUSEL_TARGET_LABELS = {
  [CAROUSEL_TARGET_PATIENT]: "Patient",
  [CAROUSEL_TARGET_PROFESSIONAL]: "Provider",
  [CAROUSEL_TARGET_FACILITY]: "Facility",
};

/** Anonymous map rotation: one slide per target per round, in this order. */
export const CAROUSEL_ANONYMOUS_ROUND_ORDER = [
  CAROUSEL_TARGET_PATIENT,
  CAROUSEL_TARGET_PROFESSIONAL,
  CAROUSEL_TARGET_FACILITY,
];

/** Minimum impressions before signup rate is trusted for ordering. */
export const CAROUSEL_MIN_IMPRESSIONS_FOR_RANKING = 50;

/**
 * Custom slide logo bounds in CareMap header carousel (`AdCarousel` — paid / optional logoUrl).
 * Images use object-fit: contain inside this box.
 */
export const MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX = 120;
export const MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX = 40;

/** Shown next to Logo URL fields in Local Carousel Ads and Carousel Global Ads admin. */
export const MAP_CAROUSEL_LOGO_URL_HELPER_TEXT = `On the map, your logo appears inside about ${MAP_CAROUSEL_LOGO_DISPLAY_MAX_WIDTH_PX}×${MAP_CAROUSEL_LOGO_DISPLAY_MAX_HEIGHT_PX}px (wider or taller art scales down; aspect ratio kept). Prefer a simple horizontal mark. PNG or SVG with a transparent background usually looks best.`;

/**
 * @param {unknown} raw
 * @returns {typeof CAROUSEL_TARGET_PATIENT | typeof CAROUSEL_TARGET_PROFESSIONAL | typeof CAROUSEL_TARGET_FACILITY}
 */
export function normalizeCarouselTarget(raw) {
  if (typeof raw !== "string") {
    return CAROUSEL_TARGET_PATIENT;
  }
  const t = raw.trim();
  if (t === "manager") {
    return CAROUSEL_TARGET_FACILITY;
  }
  if (CAROUSEL_TARGETS.includes(t)) {
    return t;
  }
  return CAROUSEL_TARGET_PATIENT;
}

export const MERGE_MODE_PAID_ONLY_THRESHOLD = "paid_only_threshold";
export const MERGE_MODE_GLOBAL_THEN_PAID = "global_then_paid";
export const MERGE_MODE_PAID_THEN_GLOBAL = "paid_then_global";

export const MAP_CAROUSEL_MERGE_MODES = [
  MERGE_MODE_PAID_ONLY_THRESHOLD,
  MERGE_MODE_GLOBAL_THEN_PAID,
  MERGE_MODE_PAID_THEN_GLOBAL,
];

export const DEFAULT_MIN_PAID_ADS = 1;
export const MAX_MIN_PAID_ADS = 50;

/** Legacy default: any paid ad ⇒ paid-only carousel. */
export const DEFAULT_MERGE_MODE = MERGE_MODE_PAID_ONLY_THRESHOLD;

/** Serializable defaults (match prior hardcoded `healthdeskSlides` in AdCarousel). */
export const HEALTHDESK_DEFAULT_SLIDE_ROWS = [
  {
    message: "Medical SuperIntelligence: Take A Stroll",
    buttonLabel: "Try for Free",
    href: "/register",
    enabled: true,
    target: CAROUSEL_TARGET_PATIENT,
  },
  {
    message: "Symptom Checker, with SuperIntelligence",
    buttonLabel: "Try for Free",
    href: "/symptomchecker",
    enabled: true,
    target: CAROUSEL_TARGET_PATIENT,
  },
  {
    message:
      "<strong>One Does Not Simply</strong> - Check Out SuperIntelligence",
    buttonLabel: "Try for Free",
    href: "/register",
    enabled: true,
    target: CAROUSEL_TARGET_PATIENT,
  },
  {
    message: "<strong>Is It Though? - Enter your symptoms</strong>",
    buttonLabel: "Try for Free",
    href: "/register",
    enabled: true,
    target: CAROUSEL_TARGET_PATIENT,
  },
  {
    message: "Discuss Health Care on <strong>Your</strong> Schedule",
    buttonLabel: "Try for Free",
    href: "/register",
    enabled: true,
    target: CAROUSEL_TARGET_PATIENT,
  },
];

export const getDefaultMapCarouselConfig = () => ({
  mergeMode: DEFAULT_MERGE_MODE,
  minPaidAds: DEFAULT_MIN_PAID_ADS,
  slides: [],
});
